import { Divider, Flex, Grid, GridItem, Spinner } from "@chakra-ui/react";
import { Article, LayoutStyle } from "../../../types";
import { NewsCard } from "../../../components/news/NewsCard";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useCallback } from "react";

export default function NewsContentGrid({
  articles,
  columnCount = 6,
  isFetchingNextPage = false,
  isVideoHub,
}: {
  articles: Article[];
  columnCount?: number;
  isFetchingNextPage?: boolean;
  isVideoHub?: boolean;
}) {
  const isSmallBreakpoint = useIsSmallBreakpoint();

  const getColumnSpan = useCallback(
    (columnCount: number, size: LayoutStyle["size"]) => {
      switch (size) {
        case "l":
        case "s":
          return columnCount / (isSmallBreakpoint ? 1 : 2);
        case "m":
          return columnCount / (isSmallBreakpoint ? 2 : 3);
        default:
          return columnCount;
      }
    },
    [isSmallBreakpoint],
  );

  const getRowGap = useCallback(
    (size: LayoutStyle["size"]) => {
      switch (size) {
        case "s":
          return isSmallBreakpoint ? "16px" : "48px";
        case "l":
          return isSmallBreakpoint ? "0px" : "48px";
        default:
          return isSmallBreakpoint ? "24px" : "48px";
      }
    },
    [isSmallBreakpoint],
  );

  const getColumnGap = useCallback(
    (size: LayoutStyle["size"]) => {
      switch (size) {
        default:
          return "32px";
      }
    },
    [isSmallBreakpoint],
  );

  let extraLarge: Article[] = [];
  let large: Article[] = [];
  let medium: Article[] = [];
  let small: Article[] = [];

  if (isVideoHub) {
    if (isSmallBreakpoint) {
      // Video hub on mobile: only large cards
      large = articles;
    } else {
      // Video hub on desktop: only medium cards
      medium = articles;
    }
  } else {
    extraLarge = isSmallBreakpoint ? [] : articles.slice(0, 1);
    large = articles.slice(isSmallBreakpoint ? 0 : 1, 5);
    medium = articles.slice(5, 17);
    small = articles.slice(17);
  }

  return (
    <Flex
      width="100%"
      flexDirection="column"
      overflow="visible hidden"
      padding={isSmallBreakpoint ? "16px" : undefined}
      paddingRight={isSmallBreakpoint ? undefined : "40px"}
      paddingBottom="40px"
      maxWidth="1200px"
      alignSelf="center"
    >
      {!isVideoHub && extraLarge.length > 0 && (
        <Grid
          templateColumns={`repeat(${columnCount}, 1fr)`}
          columnGap={getColumnGap("xl")}
          rowGap={getRowGap("xl")}
        >
          {extraLarge.map((article) => (
            <GridItem
              key={article.id}
              colSpan={getColumnSpan(columnCount, "xl")}
            >
              <NewsCard content={article} size="xl" />
            </GridItem>
          ))}
        </Grid>
      )}

      {!isVideoHub && extraLarge.length > 0 && large.length > 0 ? (
        <Divider
          marginTop={isSmallBreakpoint ? "12px" : "48px"}
          marginBottom={isSmallBreakpoint ? "12px" : "48px"}
          colorScheme="transparent.white.10"
          opacity={isSmallBreakpoint ? 0 : 1}
        />
      ) : null}

      <Grid
        templateColumns={`repeat(${columnCount}, 1fr)`}
        columnGap={getColumnGap("l")}
        rowGap={getRowGap("l")}
      >
        {large.map((article, i) => (
          <GridItem key={article.id} colSpan={getColumnSpan(columnCount, "l")}>
            <NewsCard content={article} size="l" />
            {isSmallBreakpoint && i < large.length - 1 && (
              <Divider my={"24px"} color="transparent.white.20" />
            )}
          </GridItem>
        ))}
      </Grid>

      {!isVideoHub && medium.length > 0 ? (
        <Divider
          my={isSmallBreakpoint ? "24px" : "48px"}
          colorScheme="transparent.white.20"
        />
      ) : null}

      <Grid
        templateColumns={`repeat(${columnCount}, 1fr)`}
        gap={isSmallBreakpoint ? "16px" : "24px"}
        columnGap={getColumnGap("m")}
        rowGap={getRowGap("m")}
      >
        {medium.map((article) => (
          <GridItem key={article.id} colSpan={getColumnSpan(columnCount, "m")}>
            <NewsCard content={article} size="m" />
          </GridItem>
        ))}
      </Grid>

      {small.length > 0 ? (
        <Divider
          marginTop={isSmallBreakpoint ? "24px" : "48px"}
          marginBottom={isSmallBreakpoint ? "16px" : "48px"}
          colorScheme="transparent.white.20"
        />
      ) : null}

      {!isVideoHub && (
        <Grid
          templateColumns={`repeat(${columnCount}, 1fr)`}
          gap={isSmallBreakpoint ? "16px" : "24px"}
          columnGap={getColumnGap("s")}
          rowGap={getRowGap("s")}
        >
          {small.map((article, i) => (
            <>
              <GridItem
                key={article.id}
                colSpan={getColumnSpan(columnCount, "s")}
              >
                <NewsCard
                  content={article}
                  size={isSmallBreakpoint ? "xs" : "s"}
                />
              </GridItem>

              {isSmallBreakpoint && i < small.length - 1 && (
                <GridItem colSpan={columnCount}>
                  <Divider colorScheme="transparent.white.20" width="100%" />
                </GridItem>
              )}
            </>
          ))}
          {isFetchingNextPage ? (
            <GridItem
              colSpan={getColumnSpan(3, "s")}
              width="100%"
              height="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <Spinner color="blaze.blaze" />
            </GridItem>
          ) : null}
        </Grid>
      )}

      {isVideoHub && isFetchingNextPage ? (
        <GridItem
          colSpan={getColumnSpan(3, "s")}
          width="100%"
          height="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner color="blaze.blaze" />
        </GridItem>
      ) : null}
    </Flex>
  );
}
